import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import Button, { ButtonGroup } from '@atlaskit/button';
import { Field, HelperMessage, FormSection } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import * as React from 'react';
import {
  ErrorComponents,
  InvoiceGroupFormField,
  SelectPromotionFormField,
  StatuspageAvailableAddOnsFormField,
  useFeatureToggle,
} from '@css-xp/base';
import {
  AddOn,
  Offering,
  SyntheticPricingPlan,
  ValidOfferings,
  TransactionAccount,
  Maybe,
  InvoiceGroup,
  Promotion,
  CycleInterval,
} from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { fetchAvailableOfferingsForEntitlement } from '../../../../entitlement-page/src/queries/fetch-available-offerings-for-entitlement';
import { capitalize, intersectionBy } from 'lodash';
import startCase from 'lodash/startCase';
import { RadioGroup } from '@atlaskit/radio';
import { PricingPlanStatus, PageLayer, OfferingName, PricingPlanType } from '@atlassian/cssxp-typescommon';
//import { ENABLE_VALID_PLANS_SWITCH_TO_ANNUAL } from '.../../../common/feature-flag-keys';
//import { getFeatureFlag } from '../../../../../common/security/selectors';
import { QueryResult } from '@apollo/client';
import { isSkipTrialEligible } from '@atlassian/cssxp-utils-entitlement';
import { isProductSkipTrialEligible } from '@atlassian/cssxp-utils-product';
import Checkbox from '@atlaskit/checkbox';
import { getAppropriatePaidPromotions } from '@atlassian/temp-helper';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';

export interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

interface PricingOptionsType {
  name?: string;
  value?: string;
  label?: string;
  isDisabled?: boolean;
  testId?: string;
}

interface Props {
  entitlement?: EntitlementDetails;
  transactionAccount?: Maybe<TransactionAccount>;
  isEditDisabled?: boolean;
  ispricingPlanEnabled?: boolean;
  isAnnual?: boolean;
}

const CommercialPlan: SyntheticPricingPlan[] = [
  {
    type: 'COMMERCIAL',
    status: PricingPlanStatus.ACTIVE,
    discountPercentage: 0,
  },
];

const StatuspageAvailableOfferingsFormField = ({
  entitlement,
  transactionAccount,
  isEditDisabled = false,
  ispricingPlanEnabled = false,
  isAnnual,
}: Props) => {
  const [availableAddOns, setAvailableAddOns] = React.useState<AddOn[]>([]);
  const [hasAvailableAddOns, setHasAvailableAddOns] = React.useState<boolean>(false);
  const [selectedAddOns, setSelectedAddOns] = React.useState<AddOn[]>([]);

  const [availablePricingPlan, setAvailablePricingPlan] = React.useState<any[]>([]);
  const [defaultPricingPlan, setDefaultPricingPlan] = React.useState<string | undefined | null>('');
  const [hasEligiblePricingPlan, setHasEligiblePricingPlan] = React.useState(false);
  const [isUserTierDisabled, setIsUserTierDisabled] = React.useState(true);
  const [isSkipTrialDisabled, setIsSkipTrialDisabled] = React.useState(true);
  const [isSkipTrialChecked, setIsSkipTrialChecked] = React.useState(false);
  const [isEnabled] = useFeatureToggle();

  const scheduledQty = entitlement?.scheduledChanges?.chargeQuantities?.[0]?.quantity?.toString();
  const [defaultOfferingQuantity, setDefaultOfferingQuantity] = React.useState<string>(
    scheduledQty ? scheduledQty : entitlement?.unitCount ? entitlement?.unitCount : '1'
  );

  const [statusPageAvailableOfferings, setStatusPageAvailableOfferings] = React.useState<Partial<QueryResult>>({
    loading: true,
  });

  const getDefaultBillingGroup = (invoiceGroupId: string) => {
    return (
      availableInvoiceGroups?.filter((invoiceGroup: InvoiceGroup) => {
        return invoiceGroup.id === invoiceGroupId;
      }) ?? []
    );
  };

  const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>;
  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;
  const hasSingleInvoiceGroup = availableInvoiceGroups?.length === 1;
  const defaultInvoiceGroupPricingPlan = defaultBillingGroupId
    ? getDefaultBillingGroup(defaultBillingGroupId)?.[0]?.shipToPriceEligibility
    : hasSingleInvoiceGroup
    ? availableInvoiceGroups?.[0]?.shipToPriceEligibility
    : undefined;
  const [selectedInvoiceGroupPricingPlan, setSelectedInvoiceGroupPricingPlan] = React.useState(
    defaultInvoiceGroupPricingPlan
  );
  const availablePromotions = (entitlement?.availablePromotions ?? []) as Maybe<Promotion[]>;
  const promotions = (entitlement?.scheduledChanges?.promotions
    ? entitlement?.scheduledChanges?.promotions
    : entitlement?.promotions ?? []) as Maybe<Promotion[]>;
  const paidAnnualAvailablePromotions = getAppropriatePaidPromotions(availablePromotions, CycleInterval.YEAR);
  const paidAnnualPromotions = getAppropriatePaidPromotions(promotions, CycleInterval.YEAR);
  const subscriptionId = entitlement?.subscriptionId ?? undefined;

  const getPricingEligibility = (
    pricingOptions: PricingOptionsType[],
    selectedInvoiceGroupPricingPlanValue: string | null | undefined
  ) => {
    let eligiblePricingPlan = null;
    const offeringPricingPlans = pricingOptions.map((pricingOption: PricingOptionsType) => pricingOption.value);
    const txAccountPricingPlan = (transactionAccount?.billToPartyPricingEligibilty ?? '')?.toUpperCase();
    const invoiceGroupPricingPlan = (selectedInvoiceGroupPricingPlanValue ?? '')?.toUpperCase();

    // set fianl pricing plan to txa pricing plan -
    // if txa pricing plan is set and not equal to COMMERCIAL and offering pricing plan includes txa pricing plan
    // if no txa account and invoice group pricing plan is set check if it exist in offering pricing plan
    // else always set it to COMMERCIAL
    if (
      invoiceGroupPricingPlan &&
      invoiceGroupPricingPlan !== PricingPlanType.COMMERCIAL &&
      offeringPricingPlans.includes(invoiceGroupPricingPlan)
    ) {
      eligiblePricingPlan = invoiceGroupPricingPlan;
    } else if (
      txAccountPricingPlan &&
      txAccountPricingPlan !== PricingPlanType.COMMERCIAL &&
      offeringPricingPlans.includes(txAccountPricingPlan)
    ) {
      eligiblePricingPlan = txAccountPricingPlan;
    } else {
      eligiblePricingPlan = PricingPlanType.COMMERCIAL;
    }

    setHasEligiblePricingPlan(true);
    return eligiblePricingPlan;
  };

  const setDefaultPricingPlanDetails = (
    pricingOptions: PricingOptionsType[],
    selectedInvoiceGroupPricingPlanValue: string | null | undefined
  ) => {
    const selectedPricingPlan = getPricingEligibility(pricingOptions, selectedInvoiceGroupPricingPlanValue) ?? null;
    let getDefaultPricingPlan;
    const defaultPricingPlanType =
      selectedPricingPlan ??
      capitalize(
        entitlement?.scheduledChanges?.pricingPlan
          ? entitlement?.scheduledChanges?.pricingPlan?.type
          : pricingPlanType ?? ''
      );
    // If only 1 synthetic pricing plan is available, then selecting it as default value.
    if (pricingOptions.length === 1) {
      getDefaultPricingPlan = pricingOptions[0];
    } else {
      getDefaultPricingPlan = pricingOptions.find(
        (pricingPlan) => capitalize(pricingPlan.value) === capitalize(defaultPricingPlanType)
      );
    }

    const defaultPricingPlan = selectedPricingPlan ?? getDefaultPricingPlan?.value;
    setDefaultPricingPlan(defaultPricingPlan);
  };

  const setSyntheticPricingPlan = (completeData: Partial<QueryResult>) => {
    const availableAddOnsForCurrentOffering = (completeData.data?.entitlementDetails?.offering?.availableAddOns ??
      []) as AddOn[];

    setAvailableAddOns(availableAddOnsForCurrentOffering);
    setHasAvailableAddOns(availableAddOnsForCurrentOffering.length > 0);

    if (
      isAnnual &&
      completeData.data?.entitlementDetails?.offering &&
      completeData.data?.entitlementDetails?.offering?.name === OfferingName.AUDIENCE_SPECIFIC
    ) {
      setIsUserTierDisabled(false);
    }

    /* Added Synthetic pricing Plan */
    const syntheticPricingPlans = (completeData.data?.entitlementDetails?.offering?.availableSyntheticPricingPlans ??
      []) as SyntheticPricingPlan[];
    if (intersectionBy(syntheticPricingPlans, CommercialPlan, 'type').length === 0) {
      syntheticPricingPlans.push(CommercialPlan[0]);
    }
    const pricingOptions =
      syntheticPricingPlans?.map((plan, index) => {
        return {
          name: plan?.type ?? '',
          value: plan?.type ?? '',
          label: `${startCase(plan?.type ?? '')} ${
            plan?.discountPercentage >= 0 && `(${plan?.discountPercentage}% off)`
          }`,
          isDisabled: plan?.status !== PricingPlanStatus.ACTIVE,
          testId: `pricing-plan-grp-${index}`,
        };
      }) ?? [];

    setAvailablePricingPlan(pricingOptions);
    setDefaultPricingPlanDetails(pricingOptions, selectedInvoiceGroupPricingPlan);
  };

  React.useEffect(() => {
    (async () => {
      const offerings = await graphqlClient().query({
        query: fetchAvailableOfferingsForEntitlement,
        variables: { id: entitlement?.id },
        fetchPolicy: 'no-cache',
      });

      //Enable user-tier/quantity for Audience-Specific SP offering.
      if (
        isAnnual &&
        offerings.data?.entitlementDetails?.offering &&
        offerings.data?.entitlementDetails?.offering?.name === OfferingName.AUDIENCE_SPECIFIC
      ) {
        setIsUserTierDisabled(false);
      }

      setSyntheticPricingPlan(offerings);
      setStatusPageAvailableOfferings({ ...offerings });
    })();
  }, []);

  //To Do Feature Flag
  // const validPlansSwitchToAnnualFeatureFlag = useSelector(
  //     getFeatureFlag(ENABLE_VALID_PLANS_SWITCH_TO_ANNUAL)
  // );

  const validPlansSwitchToAnnualFeatureFlag = true;
  const isUpdateUnitsFeatureEnabled = true;

  if (statusPageAvailableOfferings.loading) return <LoadingIndicator size="small" />;
  if (
    statusPageAvailableOfferings.error &&
    (!statusPageAvailableOfferings.data || !statusPageAvailableOfferings?.data?.entitlementDetails)
  ) {
    return <ErrorComponents.GenericErrorPage error={statusPageAvailableOfferings.error} />;
  }
  if (!statusPageAvailableOfferings.data || !statusPageAvailableOfferings.data?.entitlementDetails) {
    return null;
  }
  const { entitlementDetails: statuspage } = statusPageAvailableOfferings.data;
  const { availableOfferings = [], offering } = statuspage;

  const { validOfferings = [] } = statuspage;

  // Get the list of valid available offerings from CCP API(graphQL) and add in validOfferingOptions array
  const validOfferingOptions =
    // @ts-ignore
    validOfferings?.map((validOffering: ValidOfferings) => ({
      label: validOffering.offeringName ?? '',
      value: {
        id: validOffering.offeringKey ?? '',
      },
    })) ?? ([] as OfferingSelectItem[]);

  // Remove duplicates from validOffering Array
  const uniqueValiOfferingOptions = [
    ...new Map(validOfferingOptions.map((item: OfferingSelectItem) => [item.value.id, item])).values(),
  ];

  const availableOfferingOptions =
    // @ts-ignore
    availableOfferings?.map((availableOffering: Offering) => ({
      label: availableOffering.name ?? '',
      value: {
        id: availableOffering.id,
      },
    })) ?? ([] as OfferingSelectItem[]);

  if (offering && availableOfferingOptions) {
    availableOfferingOptions.unshift({
      label: offering.name,
      value: {
        id: offering.id,
      },
    });
  }

  // Add validOfferingOptions to the select menu only if switch to annual and feature flag enabled
  const OfferingOptions =
    isAnnual && validPlansSwitchToAnnualFeatureFlag ? uniqueValiOfferingOptions : availableOfferingOptions;

  const defaultOfferingOption = OfferingOptions.find(
    (offeringSelected: OfferingSelectItem) =>
      offeringSelected.value.id ===
      (entitlement?.scheduledChanges?.offering ? entitlement?.scheduledChanges.offering.id : statuspage.offering?.id)
  );

  const pricingPlanType = entitlement?.pricingPlan?.type ?? CommercialPlan[0].type;

  const updateFieldBasedOnOffering = (selectedId: string | undefined) => {
    setSelectedAddOns([]);
    setDefaultPricingPlan('');
    const availableAddOnsForOffering = ((selectedId === statuspage.offering?.id
      ? statuspage.offering?.availableAddOns
      : statuspage.availableOfferings?.find((offering: Offering) => offering?.id === selectedId)?.availableAddOns) ??
      []) as AddOn[];
    const matchingAddOns = intersectionBy(availableAddOnsForOffering, statuspage.offering?.availableAddOns ?? [], 'id');
    setAvailableAddOns(availableAddOnsForOffering);
    setHasAvailableAddOns(matchingAddOns.length > 0);
    const availablePricingPlanForOffering = ((selectedId === statuspage.offering?.id
      ? statuspage.offering?.availableSyntheticPricingPlans
      : statuspage.availableOfferings?.find((eachOffering: Offering) => eachOffering?.id === selectedId)
          ?.availableSyntheticPricingPlans) ?? []) as SyntheticPricingPlan[];
    if (intersectionBy(availablePricingPlanForOffering, CommercialPlan, 'type').length === 0) {
      availablePricingPlanForOffering.push(CommercialPlan[0]);
    }

    const selectedOffering =
      statuspage.availableOfferings?.find((offering: Offering) => offering?.id === selectedId) ??
      (statuspage.offering.id === selectedId ? statuspage.offering : null);

    //Enable user-tier/quantity for Audience-Specific SP offering.
    if (selectedOffering.name === OfferingName.AUDIENCE_SPECIFIC) {
      setIsUserTierDisabled(false);
    } else {
      setDefaultOfferingQuantity('1');
      setIsUserTierDisabled(true);
    }

    const pricingOptions =
      availablePricingPlanForOffering.map((plan, index) => {
        return {
          name: plan?.type ?? '',
          value: plan?.type ?? '',
          label: `${startCase(plan?.type ?? '')} ${
            plan?.discountPercentage >= 0 && `(${plan?.discountPercentage}% off)`
          }`,
          isDisabled: plan?.status !== PricingPlanStatus.ACTIVE,
          testId: `pricing-plan-grp-${index}`,
        };
      }) ?? [];

    setAvailablePricingPlan(pricingOptions);
    setDefaultPricingPlanDetails(pricingOptions, selectedInvoiceGroupPricingPlan);
  };

  const removeAddOn = (indexToRemove: number) => {
    setSelectedAddOns((addOns) => addOns.filter((_addOn, index) => indexToRemove !== index));
  };

  const addAdditionalAddOn = () => {
    setSelectedAddOns((addOns) => [...addOns, {}]);
  };

  const productSkipTrialEligible = isProductSkipTrialEligible(offering?.productKey);

  const evaluateSkipTrialEligibilty = (selectedId: string | undefined) => {
    const selectedOffering = statuspage.availableOfferings?.find((offering: Offering) => offering?.id === selectedId);

    if (!selectedOffering) setIsSkipTrialChecked(false);
    setIsSkipTrialDisabled(!isSkipTrialEligible(offering, selectedOffering ?? offering));
  };

  const setSkipTrial = (checked: boolean) => {
    setIsSkipTrialChecked(checked);
  };

  const setSelectedInvoiceGroupPricingPlanValue = (selectedPricingPlan: string | null | undefined) => {
    setSelectedInvoiceGroupPricingPlan(selectedPricingPlan);
    setDefaultPricingPlanDetails(availablePricingPlan, selectedPricingPlan);
  };

  return (
    <>
      <table
        id="product-detail"
        className="table-list form-table"
        data-testid="product-offering-details"
        style={{
          marginBottom: 0,
          maxWidth: '800px',
        }}
      >
        <tbody>
          <tr>
            <td className="col-30">
              <Field
                name="productId"
                defaultValue={entitlement?.entitlementNumber ?? statuspage.id}
                label="Product ID"
                isRequired
              >
                {({ fieldProps }) => <Textfield {...fieldProps} isReadOnly name="productId" />}
              </Field>
            </td>
            <td className="col-30">
              <Field<ValueType<OfferingSelectItem>, HTMLSelectElement>
                name="offeringSelectedItem"
                defaultValue={defaultOfferingOption}
                label="Plan"
                isRequired
              >
                {({ fieldProps }) => (
                  <div data-testid="product-offering-select">
                    <Select<OfferingSelectItem>
                      {...fieldProps}
                      options={OfferingOptions}
                      menuPortalTarget={document.body}
                      isSearchable={false}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: PageLayer.SIDEBAR_SELECT,
                        }),
                      }}
                      // @ts-ignore
                      onChange={(selectedItem: OfferingSelectItem) => {
                        if (selectedItem.value) {
                          updateFieldBasedOnOffering(selectedItem.value.id);
                          if (productSkipTrialEligible) evaluateSkipTrialEligibilty(selectedItem.value.id);
                          fieldProps.onChange(selectedItem);
                        }
                      }}
                      isDisabled={isEditDisabled}
                      data-testid="product-offering-details-options"
                    />
                  </div>
                )}
              </Field>
            </td>
            <td className="col-10 qty-field">
              <Field
                name="offeringQuantity"
                label={isUpdateUnitsFeatureEnabled ? 'Tier/Qty' : 'Quantity'}
                defaultValue={defaultOfferingQuantity}
                isRequired
              >
                {({ fieldProps }) => (
                  <>
                    {
                      <Textfield
                        {...fieldProps}
                        testId="offering-quantity"
                        isDisabled={isUpdateUnitsFeatureEnabled ? isUserTierDisabled : true}
                        value={defaultOfferingQuantity}
                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                          setDefaultOfferingQuantity(event.currentTarget.value);
                          fieldProps.onChange(event);
                        }}
                        type="number"
                        min="1"
                        max="99"
                      />
                    }
                  </>
                )}
              </Field>
            </td>
            <td
              className="col-10"
              data-testid="available-offering-add-ons"
              style={{
                padding: 0,
                paddingTop: '32px',
              }}
            >
              <ButtonGroup
                css={{
                  display: 'inline-flex !important',
                }}
              >
                {selectedAddOns.length === 0 && !isEditDisabled && (
                  <Tooltip content={'Add add-ons'} tag="span">
                    <Button
                      testId="add-add-ons-button"
                      style={{
                        height: '40px', // size of the input field to the left of the button
                      }}
                      isDisabled={!hasAvailableAddOns}
                      onClick={addAdditionalAddOn}
                      name="add-add-on"
                    >
                      <AddIcon label="add" size="medium" />
                    </Button>
                  </Tooltip>
                )}
              </ButtonGroup>
            </td>
          </tr>
          {hasAvailableAddOns && (
            <StatuspageAvailableAddOnsFormField
              // @ts-ignore
              availableAddOns={availableAddOns}
              // @ts-ignore
              selectedAddOns={selectedAddOns}
              removeAddOn={removeAddOn}
              addAdditionalAddOn={addAdditionalAddOn}
              layoutOptions={{
                insertEmptyFirstColumn: true,
              }}
            />
          )}
        </tbody>
      </table>
      {ispricingPlanEnabled && (
        <FormSection>
          <Field
            name="pricingPlan"
            defaultValue={defaultPricingPlan}
            isRequired
            id="pricing-plan"
            isDisabled={hasEligiblePricingPlan}
          >
            {({ fieldProps }) => <RadioGroup {...fieldProps} options={availablePricingPlan} />}
          </Field>
        </FormSection>
      )}
      {!hasAvailableAddOns && <HelperMessage>There are no add-ons available for the selected Plan</HelperMessage>}
      {productSkipTrialEligible && (
        <FormSection>
          <Field name="skipTrial">
            {({ fieldProps }) => (
              <Checkbox
                {...fieldProps}
                defaultChecked={isSkipTrialChecked}
                isChecked={isSkipTrialChecked}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  setSkipTrial(event.currentTarget.checked);
                  fieldProps.onChange(event);
                }}
                label="Skip trial"
                isDisabled={isSkipTrialDisabled}
                name="skipTrial"
                testId="skip-trial-check"
              />
            )}
          </Field>
        </FormSection>
      )}
      {isAnnual && (
        <>
          <FormSection>
            <SelectPromotionFormField
              availablePromotions={paidAnnualAvailablePromotions}
              promotions={paidAnnualPromotions}
            />
          </FormSection>
          <FormSection>
            <InvoiceGroupFormField
              productId={entitlement?.productId ?? undefined}
              availableInvoiceGroups={availableInvoiceGroups}
              entitlementBillingInvoiceGroupId={defaultBillingGroupId}
              scheduledBillingInvoiceGroupId={entitlement?.scheduledChanges?.scheduledBillingInvoiceGroupId}
              setSelectedInvoiceGroupPricingPlanValue={setSelectedInvoiceGroupPricingPlanValue}
              isDisabled={!subscriptionId ? false : !!defaultBillingGroupId}
            />
          </FormSection>
        </>
      )}
    </>
  );
};

export default StatuspageAvailableOfferingsFormField;
